/**
 * Shared pieces for unit models (ADR-0075): materials, a small kit of placed primitives,
 * a standing or seated figure, and a horse. Everything is low-poly and flat-shaded, built in
 * code; one model unit is roughly a hand's breadth, and a hex tile is 26 across.
 *
 * Model space: +x forward (the way the unit faces), +y up, +z to the unit's left.
 */

import * as T from 'three';

export type V3 = [number, number, number];

export const COL = {
  STEEL: 0xc4ccd2,
  BRONZE: 0xc49a52,
  HORSE: 0xeee7da,
  MANE: 0xb9ab95,
  HOOF: 0x4d443c,
  WOOD: 0x9a7a52,
  WOODD: 0x7a5c3c,
  DARK: 0x2f3a44,
  LEATHER: 0x7a5a3a,
  SKIN: 0xe9c6a4,
  HAIR: 0x5a4636,
  WHITE: 0xf4f1ea,
  CLOTH: 0xe8e0cf,
  BLACK: 0x2b2f33,
  IRON: 0x59636b,
  ROPE: 0xcdb48a,
  OLIVE: 0x6f7a4f,
} as const;

const materials = new Map<string, T.MeshLambertMaterial>();

/** One shared flat-shaded material per colour (and sidedness). */
export function mat(colour: number, doubleSided = false): T.MeshLambertMaterial {
  const key = `${colour}|${doubleSided}`;
  let m = materials.get(key);
  if (!m) {
    m = new T.MeshLambertMaterial({ color: colour, flatShading: true, side: doubleSided ? T.DoubleSide : T.FrontSide });
    materials.set(key, m);
  }
  return m;
}

export const shadeOf = (colour: number, f: number): number => new T.Color(colour).multiplyScalar(f).getHex();

type Paint = number | T.Material | T.Material[];
const paint = (p: Paint): T.Material | T.Material[] => (typeof p === 'number' ? mat(p) : p);

/** A unit-height, Y-axis geometry stretched and turned to run from a to b. */
function between(geometry: T.BufferGeometry, a: V3, b: V3, m: T.Material | T.Material[]): T.Mesh {
  const A = new T.Vector3(...a);
  const B = new T.Vector3(...b);
  const d = B.clone().sub(A);
  geometry.scale(1, d.length(), 1);
  const mesh = new T.Mesh(geometry, m);
  mesh.position.copy(A.clone().add(B).multiplyScalar(0.5));
  mesh.quaternion.setFromUnitVectors(new T.Vector3(0, 1, 0), d.normalize());
  return mesh;
}

export interface Kit {
  add<O extends T.Object3D>(o: O): O;
  box(w: number, h: number, d: number, x: number, y: number, z: number, p: Paint, rx?: number, ry?: number, rz?: number): T.Mesh;
  bar(a: V3, b: V3, rTop: number, rBottom: number, p: Paint, segments?: number): T.Mesh;
  ball(r: number, x: number, y: number, z: number, p: Paint, detail?: number, sx?: number, sy?: number, sz?: number): T.Mesh;
  cone(a: V3, b: V3, r: number, p: Paint, segments?: number): T.Mesh;
  wheel(x: number, y: number, z: number, r: number, colour: number, spokes?: number, rim?: number): T.Group;
  /** A flat outline in the x-y plane, extruded by `depth` along +z (0 for a two-sided sheet). */
  shape(points: Array<[number, number]>, depth: number, p: Paint, x: number, y: number, z: number, ry?: number): T.Mesh;
  disc(r: number, segments: number, colour: number, x: number, y: number, z: number, ry?: number): T.Mesh;
}

export function kit(g: T.Object3D): Kit {
  const add = <O extends T.Object3D>(o: O): O => {
    g.add(o);
    return o;
  };
  return {
    add,
    box: (w, h, d, x, y, z, p, rx = 0, ry = 0, rz = 0) => {
      const b = new T.Mesh(new T.BoxGeometry(w, h, d), paint(p));
      b.position.set(x, y, z);
      b.rotation.set(rx, ry, rz);
      return add(b);
    },
    bar: (a, b, rTop, rBottom, p, segments = 8) => add(between(new T.CylinderGeometry(rTop, rBottom, 1, segments), a, b, paint(p))),
    ball: (r, x, y, z, p, detail = 1, sx = 1, sy = 1, sz = 1) => {
      const m = new T.Mesh(new T.IcosahedronGeometry(r, detail), paint(p));
      m.position.set(x, y, z);
      m.scale.set(sx, sy, sz);
      return add(m);
    },
    cone: (a, b, r, p, segments = 8) => add(between(new T.ConeGeometry(r, 1, segments), a, b, paint(p))),
    wheel: (x, y, z, r, colour, spokes = 8, rim = 0.28) => {
      const w = new T.Group();
      w.add(new T.Mesh(new T.TorusGeometry(r, rim, 6, 20), mat(colour)));
      for (let i = 0; i < spokes; i++) {
        const s = new T.Mesh(new T.BoxGeometry(r * 2, 0.16, 0.16), mat(colour));
        s.rotation.z = (i * Math.PI) / spokes;
        w.add(s);
      }
      const hub = new T.Mesh(new T.CylinderGeometry(r * 0.22, r * 0.22, 0.6, 8), mat(colour));
      hub.rotation.x = Math.PI / 2;
      w.add(hub);
      w.position.set(x, y, z);
      return add(w);
    },
    shape: (points, depth, p, x, y, z, ry = 0) => {
      const s = new T.Shape();
      s.moveTo(points[0]![0], points[0]![1]);
      for (const [px, py] of points.slice(1)) s.lineTo(px, py);
      const geometry = depth > 0 ? new T.ExtrudeGeometry(s, { depth, bevelEnabled: false }) : new T.ShapeGeometry(s);
      const m = new T.Mesh(geometry, typeof p === 'number' ? mat(p, depth === 0) : p);
      m.position.set(x, y, z);
      m.rotation.y = ry;
      return add(m);
    },
    disc: (r, segments, colour, x, y, z, ry = 0) => {
      const m = new T.Mesh(new T.CircleGeometry(r, segments), mat(colour, true));
      m.position.set(x, y, z);
      m.rotation.y = ry;
      return add(m);
    },
  };
}

export interface PersonOptions {
  helmet?: 'hair' | 'bronze' | 'cap' | 'tricorn' | 'kettle' | 'great' | 'hood' | 'galea' | 'feather' | 'woolcap' | 'topknot' | 'morion' | 'conical' | 'brim' | 'plume' | 'kepi' | null;
  /** Breastplate colour (bronze by default). */
  cuirassColour?: number;
  /** Skin tone (default light). */
  skin?: number;
  /** Tunic colour, when not the owner's (the owner's colour then goes on a sash or trim). */
  tunic?: number;
  /** Skirt or kilt colour, when not the tunic's. */
  skirtColour?: number;
  /** A short cape behind the shoulders, in this colour. */
  cape?: number;
  legs?: number;
  boots?: number;
  sleeve?: number;
  pauldron?: number;
  belt?: number;
  skirt?: boolean;
  coat?: boolean;
  greaves?: boolean;
  cuirass?: boolean;
  crossbelts?: boolean;
  /** On horseback: legs astride instead of standing. */
  seated?: boolean;
  lHand?: V3;
  rHand?: V3;
  lElbow?: V3;
  rElbow?: V3;
}

/** A figure facing +x, feet on y = 0 (or astride a saddle when seated). */
export function person(g: T.Object3D, civ: number, o: PersonOptions = {}): Kit {
  const k = kit(g);
  const tunicColour = o.tunic ?? civ;
  const tunic = mat(tunicColour);
  const skin = o.skin ?? COL.SKIN;
  const legs = o.legs ?? COL.CLOTH;
  const boots = o.boots ?? COL.LEATHER;
  const hipY = 7.2;
  const sh = 11.4;

  for (const z of [0.72, -0.72]) {
    if (o.seated) {
      const knee: V3 = [1.1, 5.6, z * 3.3];
      k.bar([0, hipY, z * 1.1], knee, 0.62, 0.55, legs);
      k.bar(knee, [0.4, 2.6, z * 3.4], 0.55, 0.46, legs);
      k.box(1.4, 0.8, 0.8, 0.8, 2.4, z * 3.4, boots);
      continue;
    }
    const knee: V3 = [0.25, 3.9, z * 1.05];
    const ankle: V3 = [0, 1.1, z * 1.05];
    k.bar([0, hipY, z], knee, 0.62, 0.55, legs);
    k.bar(knee, ankle, 0.55, 0.44, o.greaves ? COL.BRONZE : legs);
    k.ball(0.5, knee[0], knee[1], knee[2], o.greaves ? COL.BRONZE : legs, 0);
    k.box(1.7, 1.1, 0.95, 0.35, 0.55, z * 1.05, boots);
    k.box(0.6, 0.5, 0.95, 1.05, 0.25, z * 1.05, boots);
  }

  const torso = k.bar([0, hipY - 0.2, 0], [0, sh + 0.3, 0], 1.55, 1.3, tunic, 10);
  torso.scale.z = 0.78;
  if (o.skirt !== false && !o.seated) {
    const low = o.coat ? 3.6 : 5.6;
    const skirt = o.skirtColour ?? tunicColour;
    k.bar([0, hipY + 0.6, 0], [0, low, 0], 1.42, o.coat ? 1.95 : 1.8, skirt, 10).scale.z = 0.85;
    k.bar([0, low + 0.3, 0], [0, low, 0], o.coat ? 1.93 : 1.79, o.coat ? 1.96 : 1.82, shadeOf(o.skirtColour !== undefined ? civ : skirt, 0.75), 10).scale.z = 0.86;
  }
  k.bar([0, hipY + 0.4, 0], [0, hipY + 1.0, 0], 1.4, 1.4, o.belt ?? COL.LEATHER, 10).scale.z = 0.8;
  k.box(0.25, 0.5, 0.5, 1.12, hipY + 0.7, 0, COL.BRONZE);
  if (o.crossbelts) {
    k.box(0.12, 4.6, 0.35, 1.28, 9.5, 0, COL.WHITE, 0.55);
    k.box(0.12, 4.6, 0.35, 1.28, 9.5, 0, COL.WHITE, -0.55);
  }
  if (o.cuirass) k.bar([0, hipY + 1.0, 0], [0, sh + 0.1, 0], 1.62, 1.42, o.cuirassColour ?? COL.BRONZE, 10).scale.z = 0.82;

  for (const [z, hand, elbow] of [
    [1.5, o.lHand, o.lElbow],
    [-1.5, o.rHand, o.rElbow],
  ] as Array<[number, V3 | undefined, V3 | undefined]>) {
    const S: V3 = [0, sh, z];
    const H: V3 = hand ?? [0.3, 7.6, z * 1.15];
    const E: V3 = elbow ?? [(S[0] + H[0]) / 2 + 0.2, (S[1] + H[1]) / 2, (S[2] + H[2]) / 2 + (z > 0 ? 0.35 : -0.35)];
    const sleeve = o.sleeve ?? tunicColour;
    k.ball(0.68, ...S, o.pauldron ?? civ, 1);
    k.bar(S, E, 0.5, 0.44, sleeve);
    k.ball(0.44, ...E, sleeve, 0);
    k.bar(E, H, 0.44, 0.36, sleeve);
    k.ball(0.42, ...H, skin, 1);
  }
  if (o.cape !== undefined) {
    k.shape([[-1.6, 0], [1.6, 0], [2.2, -6.2], [-2.2, -6.2]], 0.25, o.cape, -1.25, sh + 0.6, 0, Math.PI / 2);
  }

  k.bar([0, sh + 0.2, 0], [0, sh + 1.2, 0], 0.45, 0.5, skin);
  const hy = sh + 2.25;
  k.ball(1.15, 0.05, hy, 0, skin, 1, 1, 1.08, 0.95);
  k.box(0.35, 0.45, 0.3, 1.15, hy - 0.05, 0, skin);
  for (const z of [0.42, -0.42]) k.box(0.08, 0.18, 0.18, 1.08, hy + 0.25, z, COL.DARK);

  switch (o.helmet) {
    case 'hair':
      k.ball(1.2, -0.15, hy + 0.35, 0, COL.HAIR, 1, 1, 0.8, 0.98);
      break;
    case 'bronze':
      k.ball(1.28, 0, hy + 0.3, 0, COL.BRONZE, 1, 1, 0.85, 1);
      k.box(0.5, 1.3, 0.35, 0.95, hy - 0.1, 0, COL.BRONZE);
      for (const z of [0.85, -0.85]) k.box(0.9, 1.2, 0.2, 0.55, hy - 0.45, z, COL.BRONZE);
      k.bar([-0.9, hy + 1.3, 0], [1.0, hy + 1.5, 0], 0.08, 0.08, COL.BRONZE);
      k.shape([[-1.6, 0], [-1.2, 1.1], [0, 1.4], [1.1, 1.0], [1.2, 0]], 0.35, civ, 0, hy + 1.3, -0.17);
      break;
    case 'cap':
      k.ball(1.22, 0, hy + 0.35, 0, COL.LEATHER, 1, 1, 0.75, 1);
      k.bar([0, hy + 0.05, 0], [0, hy + 0.25, 0], 1.3, 1.3, COL.LEATHER, 10);
      break;
    case 'tricorn': {
      const t = k.bar([0, hy + 0.6, 0], [0, hy + 1.05, 0], 2.0, 2.0, COL.BLACK, 3);
      t.rotation.y = Math.PI / 2;
      k.ball(1.05, 0, hy + 1.1, 0, COL.BLACK, 1, 1, 0.7, 1);
      k.ball(0.25, 1.0, hy + 0.95, 0, COL.WHITE, 0);
      break;
    }
    case 'kettle':
      k.bar([0, hy + 0.45, 0], [0, hy + 0.6, 0], 2.0, 2.0, COL.OLIVE, 12);
      k.ball(1.2, 0, hy + 0.55, 0, COL.OLIVE, 1, 1, 0.7, 1);
      break;
    case 'great':
      k.bar([0, hy - 1.3, 0], [0, hy + 1.2, 0], 1.35, 1.4, COL.STEEL, 10);
      k.ball(1.36, 0, hy + 1.2, 0, COL.STEEL, 1, 1, 0.4, 1);
      k.box(0.12, 0.22, 1.9, 1.33, hy + 0.2, 0, COL.DARK);
      k.box(0.12, 1.4, 0.14, 1.36, hy - 0.6, 0, COL.DARK);
      k.shape([[-1.4, 0], [-0.9, 1.6], [0, 2.1], [0.9, 1.6], [1.3, 0]], 0.4, civ, 0, hy + 1.55, -0.2);
      break;
    case 'hood':
      k.ball(1.3, -0.15, hy + 0.25, 0, COL.LEATHER, 1, 1, 0.95, 1.05);
      k.bar([-0.3, hy - 1.2, 0], [-0.1, hy + 0.2, 0], 1.25, 1.35, COL.LEATHER, 8);
      break;
    case 'galea':
      k.ball(1.3, 0, hy + 0.3, 0, COL.STEEL, 1, 1, 0.85, 1);
      for (const z of [0.95, -0.95]) k.box(0.9, 1.3, 0.18, 0.4, hy - 0.5, z, COL.STEEL);
      k.box(1.6, 0.2, 2.8, -0.9, hy - 0.4, 0, COL.STEEL);
      k.shape([[-1.8, 0], [-1.5, 0.9], [1.5, 0.9], [1.8, 0]], 0.5, civ, 0, hy + 1.2, -0.25, Math.PI / 2);
      break;
    case 'feather':
      k.ball(1.2, -0.15, hy + 0.35, 0, COL.HAIR, 1, 1, 0.8, 0.98);
      k.bar([0, hy + 0.6, 0], [0, hy + 0.8, 0], 1.22, 1.22, civ, 10);
      k.shape([[0, 0], [0.5, 1.2], [0.3, 3.0], [-0.1, 1.4]], 0, COL.WHITE, -0.6, hy + 0.9, 0, Math.PI / 2);
      break;
    case 'woolcap':
      k.ball(1.25, 0, hy + 0.3, 0, civ, 1, 1, 0.9, 1);
      for (const z of [1.0, -1.0]) k.box(0.7, 1.6, 0.2, 0, hy - 0.6, z, civ);
      k.bar([0, hy + 1.3, 0], [0, hy + 1.6, 0], 0.3, 0.3, COL.WHITE, 6);
      break;
    case 'topknot':
      k.ball(1.2, -0.15, hy + 0.35, 0, COL.BLACK, 1, 1, 0.8, 0.98);
      k.ball(0.55, -0.4, hy + 1.35, 0, COL.BLACK, 1);
      k.bar([-0.4, hy + 1.2, 0], [-0.4, hy + 1.0, 0], 0.6, 0.6, civ, 8);
      break;
    case 'morion':
      k.ball(1.25, 0, hy + 0.45, 0, COL.STEEL, 1, 1, 0.9, 1);
      k.bar([0, hy + 0.2, 0], [0, hy + 0.35, 0], 2.0, 2.0, COL.STEEL, 12).scale.z = 0.7;
      k.shape([[-1.2, 0], [-0.6, 1.0], [0.6, 1.0], [1.2, 0]], 0.18, COL.STEEL, 0, hy + 1.1, -0.09);
      k.ball(0.3, -0.9, hy + 1.3, 0, civ, 0);
      break;
    case 'conical':
      k.cone([0, hy + 0.2, 0], [0, hy + 2.6, 0], 1.3, COL.STEEL, 8);
      k.box(0.25, 1.3, 0.2, 1.2, hy - 0.1, 0, COL.STEEL);
      k.bar([-0.2, hy - 0.6, 0], [-0.4, hy - 1.6, 0], 1.25, 1.35, 0x9aa3a8, 8);
      break;
    case 'brim':
      k.bar([0, hy + 0.55, 0], [0, hy + 0.7, 0], 2.2, 2.2, 0x8a6a45, 12);
      k.bar([0, hy + 0.7, 0], [0, hy + 1.8, 0], 1.0, 1.2, 0x8a6a45, 10);
      k.bar([0, hy + 0.75, 0], [0, hy + 1.05, 0], 1.22, 1.22, civ, 10);
      break;
    case 'kepi':
      k.bar([0, hy + 0.3, 0], [0, hy + 1.7, 0], 1.05, 1.22, civ, 10);
      k.bar([0, hy + 1.7, 0], [0, hy + 1.85, 0], 1.1, 1.1, COL.BLACK, 10);
      k.box(1.2, 0.15, 1.8, 1.0, hy + 0.4, 0, COL.BLACK, 0, 0, -0.25);
      k.ball(0.2, 1.1, hy + 1.0, 0, COL.BRONZE, 0);
      break;
    case 'plume':
      k.ball(1.3, 0, hy + 0.35, 0, COL.BRONZE, 1, 1, 0.9, 1);
      k.box(1.2, 0.2, 2.4, 1.1, hy + 0.05, 0, COL.BLACK);
      k.bar([-0.5, hy + 1.2, 0], [-1.6, hy + 2.4, 0], 0.35, 0.6, civ, 6);
      k.bar([-1.6, hy + 2.4, 0], [-2.6, hy + 1.2, 0], 0.6, 0.3, civ, 6);
      break;
    default:
      break;
  }
  return k;
}

/** An ox facing +x: a heavier horse with horns and a dark coat. */
export function ox(g: T.Object3D, civ: number): Kit {
  const k = horse(g, civ, { coat: 0x8a6a4a, bare: true });
  for (const z of [1, -1]) {
    k.bar([8.6, 14.4, z * 0.6], [8.8, 14.8, z * 2.2], 0.32, 0.22, COL.CLOTH, 5);
    k.bar([8.8, 14.8, z * 2.2], [9.6, 16.4, z * 2.6], 0.22, 0.06, COL.CLOTH, 5);
  }
  k.ball(2.0, 3.2, 10.6, 0, 0x8a6a4a, 1, 1.4, 0.7, 0.9); // hump
  k.ball(1.6, 5.6, 7.0, 0, 0x7a5c3e, 1, 0.8, 1.0, 0.5); // dewlap
  k.box(1.0, 0.5, 4.2, 6.2, 12.4, 0, COL.WOOD); // yoke
  return k;
}

export interface HorseOptions {
  coat?: number;
  caparison?: boolean;
  /** No saddle (a draught horse or ox). */
  bare?: boolean;
  /** A saddle cloth in the owner's colour. */
  saddleCloth?: boolean;
}

/** A horse facing +x with saddle, stirrups, bridle and reins; hooves on y = 0. */
export function horse(g: T.Object3D, civ: number, o: HorseOptions = {}): Kit {
  const k = kit(g);
  const H = o.coat ?? COL.HORSE;
  for (const [x, z, bend] of [
    [3.7, 1.15, 0.3],
    [3.7, -1.15, -0.2],
    [-3.9, 1.15, -0.3],
    [-3.9, -1.15, 0.2],
  ] as Array<[number, number, number]>) {
    const back = x < 0;
    const knee: V3 = [x + (back ? -0.5 : 0.2) + bend * 0.3, 3.6, z];
    const fetlock: V3 = [x + bend, 1.0, z];
    k.bar([x, 7.2, z], knee, back ? 0.85 : 0.6, 0.42, H);
    k.ball(0.45, ...knee, H, 0);
    k.bar(knee, fetlock, 0.38, 0.33, H);
    k.bar(fetlock, [fetlock[0] + 0.15, 0, z], 0.48, 0.52, COL.HOOF);
  }
  k.bar([-4.6, 8.6, 0], [4.6, 8.8, 0], 2.1, 2.25, H, 12).scale.z = 0.95;
  k.ball(2.35, 4.4, 8.7, 0, H, 1, 1.05, 1, 0.92);
  k.ball(2.45, -4.4, 8.9, 0, H, 1, 1.1, 1, 0.98);
  k.bar([5.0, 9.4, 0], [7.6, 13.8, 0], 1.0, 1.7, H, 10);
  k.bar([11.4, 11.4, 0], [8.1, 14.3, 0], 0.66, 1.02, H, 8).scale.z = 0.85;
  k.ball(0.75, 11.3, 11.5, 0, H, 1, 1, 0.85, 0.85);
  for (const z of [0.42, -0.42]) k.cone([8.3, 14.7, z], [8.0, 16.1, z], 0.3, H, 5);
  for (const z of [0.66, -0.66]) k.box(0.22, 0.3, 0.12, 9.6, 13.4, z, COL.DARK);
  for (const z of [0.45, -0.45]) k.box(0.2, 0.2, 0.1, 11.9, 11.6, z, COL.DARK);
  k.bar([4.8, 11.3, 0], [8.2, 15.0, 0], 0.35, 0.5, COL.MANE, 4).scale.x = 1.8;
  k.bar([-6.5, 9.8, 0], [-7.1, 7.0, 0], 0.5, 0.7, COL.MANE, 6);
  k.bar([-7.1, 7.0, 0], [-7.0, 4.0, 0], 0.7, 0.35, COL.MANE, 6);
  for (const z of [0.62, -0.62]) k.bar([10.3, 12.0, z], [9.0, 13.9, z], 0.07, 0.07, COL.LEATHER, 4);
  for (const z of [0.7, -0.7]) k.bar([10.7, 11.7, z], [2.0, 11.6, z * 1.7], 0.06, 0.06, COL.LEATHER, 4);

  if (o.caparison) {
    const cloth = mat(civ);
    k.bar([-5.1, 8.4, 0], [5.0, 8.6, 0], 2.35, 2.45, cloth, 12);
    k.box(10.2, 2.4, 4.95, 0, 6.4, 0, civ);
    k.box(10.3, 0.3, 5.0, 0, 5.25, 0, shadeOf(civ, 0.7));
    for (const x of [-4, -1.4, 1.2, 3.8]) k.box(0.12, 2.4, 5.0, x, 6.4, 0, shadeOf(civ, 0.85));
    for (const z of [2.5, -2.5]) {
      k.ball(1.0, 0, 7.4, z, COL.WHITE, 1, 1, 1, 0.15);
      k.box(0.25, 1.2, 0.2, 0, 7.4, z * 1.02, civ);
      k.box(1.2, 0.25, 0.2, 0, 7.4, z * 1.02, civ);
    }
    k.bar([7.6, 13.8, 0], [5.0, 9.4, 0], 1.1, 1.8, cloth, 10);
  }
  if (o.bare) return k;
  if (o.saddleCloth) {
    k.box(5.2, 0.25, 4.8, -0.4, 10.62, 0, civ);
    k.box(5.4, 2.6, 0.12, -0.4, 9.4, 2.32, civ);
    k.box(5.4, 2.6, 0.12, -0.4, 9.4, -2.32, civ);
    k.box(5.4, 0.25, 0.14, -0.4, 8.2, 2.34, COL.WHITE);
    k.box(5.4, 0.25, 0.14, -0.4, 8.2, -2.34, COL.WHITE);
  }
  k.box(3.6, 0.7, 4.6, -0.4, 10.9, 0, COL.LEATHER);
  k.box(0.5, 1.0, 3.0, 1.2, 11.4, 0, COL.LEATHER);
  k.box(0.5, 1.3, 3.4, -2.0, 11.5, 0, COL.LEATHER);
  for (const z of [2.3, -2.3]) {
    k.bar([-0.3, 10.8, z], [-0.3, 7.2, z * 1.05], 0.05, 0.05, COL.LEATHER, 4);
    k.box(0.8, 0.25, 0.6, -0.3, 7.0, z * 1.05, COL.IRON);
  }
  return k;
}
